/**
 * useKeyboardFit — видимая область над экранной клавиатурой для fixed-слоёв.
 *
 * ПРОБЛЕМА. iOS/iPadOS при открытии клавиатуры не сжимает layout viewport:
 * fixed-слой на inset:0 остаётся прежней высоты, а его нижняя часть уходит ПОД
 * клавиатуру. Центрированный попап (выбор исполнителей, новая единица) ложится
 * серединой на клавиатуру, нижний лист — целиком под неё. На iPad в
 * горизонтали клавиатура занимает почти половину экрана, и список людей
 * перекрыт.
 *
 * РЕШЕНИЕ. Единственный источник настоящей видимой области — visualViewport
 * (тот же приём, что в шторке «Канала» и подсказках прайса). Хук отдаёт
 * { top, height } видимой области, пока клавиатура открыта, и null — когда её
 * нет: тогда слой остаётся на inset:0 без единого лишнего перерасчёта.
 *
 * Использование — поверх inset:0 оверлея:
 *   const kb = useKeyboardFit();
 *   <div style={{ position: 'fixed', inset: 0, ...(kb ? { top: kb.top, height: kb.height, bottom: 'auto' } : null) }}>
 *
 * Порог 120px отсекает «дыхание» адресной строки Safari (≈50px) и зум щипком
 * не считает клавиатурой: настоящая клавиатура iPad — от 300px.
 */
import { useEffect, useState } from 'react';

const KEYBOARD_MIN_PX = 120;

export default function useKeyboardFit() {
  const [box, setBox] = useState(null);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const apply = () => {
      const covered = window.innerHeight - vv.height;
      const next = covered > KEYBOARD_MIN_PX
        ? { top: Math.max(0, Math.round(vv.offsetTop)), height: Math.round(vv.height) }
        : null;
      // scroll у visualViewport летит пачками — без сравнения это перерисовка
      // на каждый кадр.
      setBox((prev) => {
        if (prev === next) return prev;
        if (prev && next && prev.top === next.top && prev.height === next.height) return prev;
        return next;
      });
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, []);

  return box;
}

/** Стиль-надстройка для оверлея: пусто, пока клавиатуры нет. */
export function keyboardFitStyle(kb) {
  return kb ? { top: kb.top, height: kb.height, bottom: 'auto' } : null;
}
